import { useEffect, useRef, useState } from "react";
import type { ExfilConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { useSpeaking } from "../lib/useSpeaking";

/**
 * The attacker exfiltration console (hacker POV — no scoring). Using the hijacked
 * WFH session, the operator copies the victim's accessible network shares, PLANTS
 * fresh lure files on the public share (worming the next victims), then a "new
 * connections" toast shows the spread. Full-screen cinematic over the dark hacker
 * laptop; reuses the publish-pipeline visual language. completeOn `continue`.
 */
type Stage = "copy" | "plant" | "spread";

const TYPE_ICON: Record<string, string> = {
  exe: "\u2699",
  doc: "\u{1F4C4}",
  pdf: "\u{1F4D5}",
  zip: "\u{1F5DC}",
  xls: "\u{1F4D7}",
};

export function Exfil({ config, onContinue }: { config: ExfilConfig; onContinue: () => void }) {
  const shares = config.shares ?? [];
  const planted = config.plantedFiles ?? [];
  const [stage, setStage] = useState<Stage>("copy");
  const [copyIdx, setCopyIdx] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [plantIdx, setPlantIdx] = useState(-1);
  const [toast, setToast] = useState(false);
  const mounted = useRef(true);
  const speaking = useSpeaking();

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.line) void audio.prefetchSpeech(config.line, { voice: config.voiceId || "onyx", lang: config.language || "en-US" });
    mounted.current = true;
    if (config.line) {
      window.setTimeout(
        () => mounted.current && audio.speak(config.line!, { voice: config.voiceId || "onyx", lang: config.language || "en-US" }),
        160
      );
    }
    const timers: number[] = [];
    // copy each share
    shares.forEach((_, i) => {
      timers.push(window.setTimeout(() => mounted.current && setCopyIdx(i), 500 + i * 850));
    });
    const copyTotal = 500 + shares.length * 850 + 400;
    const t0 = performance.now();
    const tick = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - t0) / copyTotal);
      if (mounted.current) setProgress(Math.round(k * 100));
      if (k >= 1) window.clearInterval(tick);
    }, 80);
    timers.push(tick);

    // then plant lures
    timers.push(
      window.setTimeout(() => {
        if (!mounted.current) return;
        setCopyIdx(shares.length);
        setStage("plant");
      }, copyTotal + 200)
    );
    planted.forEach((_, i) => {
      timers.push(
        window.setTimeout(() => mounted.current && setPlantIdx(i), copyTotal + 700 + i * 700)
      );
    });
    // then the spread toast
    timers.push(
      window.setTimeout(() => {
        if (!mounted.current) return;
        setPlantIdx(planted.length);
        setStage("spread");
        setToast(true);
      }, copyTotal + 900 + planted.length * 700)
    );

    return () => {
      mounted.current = false;
      audio.cancelSpeech();
      timers.forEach((t) => {
        window.clearTimeout(t);
        window.clearInterval(t);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="exfil">
      <div className="exfil-app">
        <div className="exfil-titlebar">
          <span className="exfil-dot r" />
          <span className="exfil-dot y" />
          <span className="exfil-dot g" />
          <span className="exfil-app-name">{config.appName ?? "Remote session — network shares"}</span>
          <span className="exfil-path">{config.path ?? "\\\\VELTORA\\Shares"}</span>
        </div>

        {toast && (
          <div className="exfil-toast">
            <span className="exfil-toast-dot" />
            {config.spreadToast ?? `New inbound connections from ${config.victimOrg ?? "the"} network`}
          </div>
        )}

        <div className="exfil-body">
          {/* Left: shares being copied out */}
          <div className="exfil-left">
            <div className="exfil-card-h">Exfiltrating shares</div>
            <ul className="dfgen-steps">
              {shares.map((s, i) => {
                const state = i < copyIdx || stage !== "copy" ? "done" : i === copyIdx ? "active" : "wait";
                return (
                  <li key={i} className={`dfgen-step ${state}`}>
                    <span className="dfgen-step-ico">{state === "done" ? "\u2713" : ""}</span>
                    <span className="exfil-share-name">{"\u{1F4C1}"} {s.name}</span>
                    <span className="exfil-share-size">{s.size}</span>
                  </li>
                );
              })}
            </ul>
            <div className="dfgen-progress">
              <div className="dfgen-progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <div className="dfgen-progress-label">
              {stage === "copy" ? `Copying\u2026 ${progress}%` : "Exfiltration complete"}
            </div>
          </div>

          {/* Right: lure files being planted on the public share */}
          <div className="exfil-right">
            <div className="exfil-card-h">Planting lures on \\Public</div>
            {stage === "copy" ? (
              <div className="exfil-empty">Waiting for exfiltration to finish\u2026</div>
            ) : (
              <ul className="exfil-files">
                {planted.map((f, i) => {
                  const shown = stage === "spread" || i <= plantIdx;
                  return (
                    <li key={i} className={`exfil-file ${shown ? "in" : ""}`}>
                      <span className="exfil-file-ico" aria-hidden>{TYPE_ICON[f.type ?? "doc"] ?? "\u{1F4C4}"}</span>
                      <span className="exfil-file-name">{f.name}</span>
                      {shown && <span className="exfil-file-tag">planted</span>}
                    </li>
                  );
                })}
              </ul>
            )}

            {stage === "spread" && (
              <button
                type="button"
                className={`btn exfil-continue ${speaking ? "speaking" : ""}`}
                onClick={onContinue}
                disabled={speaking}
              >
                {config.continueLabel ?? "What happens next?"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
